// Package Imports
import axios, {
  type AxiosError,
  type AxiosInstance,
  type InternalAxiosRequestConfig,
} from 'axios'

// Core Imports
import { config } from '@/core/config/env'
import { authEvents } from '@/core/lib/auth-events'
import { queryClient } from '@/core/lib/query-client'
import { notify } from '@/core/lib/ui-bus'

let tokenRefreshPromise: Promise<string | null> | null = null

const NOTIFY_DEDUPE_MS = 2500
const lastNotifiedAt: Record<string, number> = {}

const notifyOnce = (key: string, fn: () => void) => {
  const now = Date.now()
  if (now - (lastNotifiedAt[key] ?? 0) < NOTIFY_DEDUPE_MS) return
  lastNotifiedAt[key] = now
  fn()
}

export const requestInterceptor = (config: InternalAxiosRequestConfig) => {
  const accessToken = queryClient.getQueryData<string>(['accessToken'])
  if (accessToken) config.headers.Authorization = `Bearer ${accessToken}`
  return config
}

const runRefresh = (refreshApi: AxiosInstance): Promise<string | null> => {
  if (!tokenRefreshPromise) {
    tokenRefreshPromise = refreshApi
      .get('/admin/auth/access-token')
      .then((res) => res.data?.data?.access_token ?? null)
      .catch(() => null)
      .finally(() => {
        tokenRefreshPromise = null
      })
  }
  return tokenRefreshPromise
}

export const responseInterceptor = async (
  error: AxiosError & {
    config?: InternalAxiosRequestConfig & { _retry?: boolean }
  },
  apiInstance: AxiosInstance,
  refreshApi: AxiosInstance
) => {
  const originalRequest = error.config
  if (!error.response || !originalRequest) return Promise.reject(error)

  const status = error.response.status

  if (status === 429) {
    notifyOnce('429', () =>
      notify.error({
        message: 'Çok fazla istek',
        description:
          'Çok fazla istek gönderildi. Lütfen biraz sonra tekrar deneyin.',
        placement: 'topRight',
        duration: 3,
      })
    )
    return Promise.reject(error)
  }

  if (status === 401 && !originalRequest._retry) {
    originalRequest._retry = true
    const newToken = await runRefresh(refreshApi)
    if (newToken) {
      queryClient.setQueryData(['accessToken'], newToken)
      originalRequest.headers.Authorization = `Bearer ${newToken}`
      return apiInstance(originalRequest)
    }
    authEvents.emit('signed-out')
    return Promise.reject(error)
  }

  if (status === 403) {
    notifyOnce('403', () =>
      notify.error({
        message: 'Yetkilendirme Hatası',
        description: 'Bu işlem için yetkiniz yok.',
        placement: 'topRight',
        duration: 3,
      })
    )
    authEvents.emit('forbidden')
    return Promise.reject(error)
  }

  return Promise.reject(error)
}

export const api = axios.create({
  baseURL: config.apiUrl,
  withCredentials: true,
})

const refreshApi = axios.create({
  baseURL: config.apiUrl,
  withCredentials: true,
})

/* c8 ignore next 3 */
api.interceptors.request.use(requestInterceptor, (error) =>
  Promise.reject(error)
)
/* c8 ignore next 4 */
api.interceptors.response.use(
  (res) => res,
  (error) => responseInterceptor(error, api, refreshApi)
)
